import { CHROME, INK, PRIMARY } from '../../Theme/palette';
import { EmailSendModal, SharedTable } from '@components';
import { CalendarOutlined, DashboardOutlined, LinkOutlined, MailOutlined, ToolOutlined } from '@ant-design/icons';
import { IServiceHistoriesHook } from '@models';
import { ServiceHistory } from '@services';
import * as Services from '@services';
import { Button, Table, Tag } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { useView } from '@hooks';
import { observer } from 'mobx-react';
import { Columns } from './VehicleHistoryTable.columns';

interface IProps {
  historyHook: IServiceHistoriesHook;
  emailModalOpen: boolean;
  email: string;
  emailError: string | null;
  emailSending: boolean;
  handleEmailCancel: () => void;
  handleEmailChange: (val: string) => void;
  handleEmailSend: () => void;
  emailExtraContent?: React.ReactNode;
  onViewAll: () => void;
  onSendEmail: () => void;
  selectedStatuses: string[];
  compact?: boolean;
}

const PAGE_SIZE = 10;

const CompactHistoryList: React.FC<{
  historyHook: IServiceHistoriesHook;
}> = ({ historyHook }) => {
  const [items, setItems] = useState<ServiceHistory[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const skipRef = useRef(0);
  const vinRef = useRef('');

  useEffect(() => {
    const incoming = historyHook?.dataSource ?? [];
    if (incoming.length > 0) {
      setItems(incoming);
      setTotalCount(historyHook.totalCount);
      skipRef.current = incoming.length;
      vinRef.current = incoming[0].vin;
    }
  }, [historyHook.dataSource, historyHook.totalCount]);

  const hasMore = items.length < totalCount;

  const handleLoadMore = async () => {
    if (!vinRef.current || loading) return;
    setLoading(true);
    try {
      const result = await Services.ServiceHistoryService.getAll({
        vin: vinRef.current,
        skipCount: skipRef.current,
        maxResultCount: PAGE_SIZE,
      });
      setItems((prev) => [...prev, ...result.items]);
      setTotalCount(result.totalCount);
      skipRef.current += result.items.length;
    } finally {
      setLoading(false);
    }
  };

  if (!items.length && !historyHook.loading) {
    return <div style={{ textAlign: 'center', color: '#8c8c8c', padding: 24 }}>No service history</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map((history: ServiceHistory) => {
        const services = [
          ...new Set((history.reminderServices ?? []).map((s) => s.reminderName).filter(Boolean)),
        ];
        return (
          <div
            key={history.id}
            style={{
              background: '#fff',
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              padding: '10px 14px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <CalendarOutlined style={{ color: PRIMARY, fontSize: 13 }} />
              <strong style={{ fontSize: 13 }}>
                {dayjs(history.creationTime).format('DD/MM/YYYY')}
              </strong>
              {history.mileage > 0 && (
                <Tag style={{ margin: 0, fontSize: 11 }} title="Mileage">
                  <DashboardOutlined /> {history.mileage.toLocaleString()} mi
                </Tag>
              )}
            </div>
            {services.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {services.map((name, i) => (
                  <Tag
                    key={i}
                    icon={<ToolOutlined />}
                    style={{
                      margin: 0,
                      fontSize: 12,
                      borderRadius: 4,
                      background: CHROME.bg,
                      borderColor: CHROME.border,
                      color: INK,
                    }}
                  >
                    {name}
                  </Tag>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {hasMore && (
        <Button
          block
          type="text"
          loading={historyHook.loading}
          onClick={handleLoadMore}
          style={{ marginTop: 4, color: PRIMARY, fontWeight: 500 }}
        >
          Load more ({items.length} of {historyHook.totalCount})
        </Button>
      )}
    </div>
  );
};

const VehicleHistoryTable: React.FC<IProps> = ({
  historyHook,
  emailModalOpen,
  email,
  emailError,
  emailSending,
  handleEmailCancel,
  handleEmailChange,
  handleEmailSend,
  emailExtraContent,
  onViewAll,
  onSendEmail,
  selectedStatuses,
  compact = false,
}) => {
  const { isMobile } = useView();

  const toolbar = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 12 }}>
      <Button icon={<LinkOutlined />} onClick={onViewAll}>
        View All
      </Button>
      <Button type="primary" icon={<MailOutlined />} onClick={onSendEmail}>
        Send Email
      </Button>
    </div>
  );

  const emailModal = (
    <EmailSendModal
      open={emailModalOpen}
      title="Send service history email"
      emailValue={email}
      setEmailValue={handleEmailChange}
      emailError={emailError}
      onConfirm={handleEmailSend}
      onCancel={handleEmailCancel}
      confirmText="Send"
      okButtonProps={{ disabled: !!emailError || selectedStatuses.length === 0, loading: emailSending }}
      extraContent={emailExtraContent}
    />
  );

  return (
    <>
      {compact ? (
        <>
          {toolbar}
          <CompactHistoryList historyHook={historyHook} />
          {emailModal}
        </>
      ) : isMobile ? (
        <>
          {toolbar}
          {emailModal}
        </>
      ) : (
        <>
          {toolbar}
          <SharedTable
            {...historyHook}
            columns={Columns()}
            summary={() => (
              <Table.Summary fixed="top">
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} />
                  <Table.Summary.Cell index={1} />
                  <Table.Summary.Cell index={2} />
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
          {emailModal}
        </>
      )}
    </>
  );
};

export default observer(VehicleHistoryTable);
